import { EmployeeListItemMinDto } from './employees';
import { ExpenseDto } from './expenses';
import { EditIntentTypeCode, InvoiceStatusType, InvoiceTransactionActionType } from './invoices';
import { MatterBillingSummaryDto, MatterListItemMin, RemunerationCalcType } from './matters';
import { NamedEntityMinDto } from './other';
import { HourlyRate } from './pricelists';
import { TimecardBasicDto } from './timecards';

export enum LimitType {
    ALL = 'ALL',
    PREV_MONTH = 'PREV_MONTH',
}

export interface InvoiceTimecardsAndExpensesDto {
    invoiceTimecards: InvoiceTimecard[];
    invoiceExpenses: InvoiceExpense[];
}

export interface InvoiceMatterSummary {
    matterBillingSummary: MatterBillingSummaryDto;
    matterId: number;
    matterNumber: string;
    matterName: string;
    matterAssignments: NamedEntityMinDto[];
    fullName: string;
    adjustmentAmount?: number;
    feeArrangementTypeCode: string;
    feeArrangementAmount: number;
    discountRate: number;
    invoiceTimecards: InvoiceTimecard[];
    invoiceExpenses: InvoiceExpense[];
    invoiceRows: InvoiceRow[];
    invoiceAssignments: InvoiceTimecardAssignment[];
    adjustmentAmountForFees?: number;
    adjustmentAmountForExpensesWithoutVat?: number;
    adjustmentAmountForExpensesWithVat?: number;
    capRemainingBeforeInvoice?: number;
    areCostsCapped?: boolean;
    areCostsDiscounted?: boolean;
    calculationLog?: InvoiceMatterCalculationLog;
    isMatterStatusUpdatable: boolean;
    hasMatterFeeArrangementTypeChanged: boolean;
    rates?: HourlyRate[];
    // The cached figures of the case's price list in force today.
    minHourlyPrice?: string;
    maxHourlyPrice?: string;
    avgHourlyPrice?: string;
    pricelistCurrencyTypeCode?: string;
    pricelistName?: string;
}

interface InvoiceMatterCalculationLogBase {
    invoiceCurrencyTypeCode: string;
    cap: {
        appliedCap: number;
        includesCosts: boolean;
        remainingBeforeInvoice: number;
    };
    rows: {
        fixedFees: number;
        recurringFees: number;
        prepayments: number;
    };
    fees: {
        usedTimeInHours: number;
        billedTimeInHours: number;
        totalWithoutVat: number;
        totalBeforeMatterAdjustments: number;
        totalAfterMatterAdjustments: number;
        matterCashAdjustment: number;
        matterDiscountRate: number;
        matterDiscountAmount: number;
    };
    expensesWithoutVat: {
        totalWithoutVat: number;
        totalBeforeMatterAdjustments: number;
        totalAfterMatterAdjustments: number;
        matterCashAdjustment: number;
        discountApplicable: boolean;
        matterDiscountRate: number;
        matterDiscountAmount: number;
    };
    expensesWithVat: {
        totalWithoutVat: number;
        totalBeforeMatterAdjustments: number;
        totalAfterMatterAdjustments: number;
        matterCashAdjustment: number;
        discountApplicable: boolean;
        matterDiscountRate: number;
        matterDiscountAmount: number;
    };
    total: {
        totalWithoutVat: number;
        totalBeforeMatterAdjustments: number;
        totalAfterMatterAdjustments: number;
        matterCashAdjustment: number;
        matterDiscountRate: number;
        matterDiscountAmount: number;
    };
}

export interface InvoiceMatterCalculationLog extends InvoiceMatterCalculationLogBase {
    paymentCurrency?: InvoiceMatterCalculationLogBase;
}

export interface InvoiceTimecardAssignment {
    assignmentId?: number;
    assignmentName?: string;
    assignmentDescription?: string;
    billedHours?: number;
    usedHours?: number;
    totalBeforeAdjustments?: number;
    amountAdjustmentDiscount?: number;
    amountAdjustmentCash?: number;
    totalAfterAdjustments?: number;
    totalAmountPaymentCurrency?: number;
    totalCap?: number;
    remainingCap?: number;
    capAdjustment?: number;
    timeCardDetails: InvoiceTimecard[];
}

export interface InvoiceExpense {
    id: number;
    employee: EmployeeListItemMinDto;
    expenseDate: string;
    description?: string;
    isNonBillable: boolean;
    expenseTypeCode: string;
    expenseAmount: number;
    expenseAmountInPaymentCurrency: number | null;
    currencyTypeCode: string;
    discountAmount?: number;
    discountTypeCode?: string;
    matter: MatterListItemMin;
    newMatter: MatterListItemMin;
    editIntentTypeCode?:
        | EditIntentTypeCode.MOVE_TO_ANOTHER_CASE
        | EditIntentTypeCode.MARK_AS_NONBILLABLE
        | EditIntentTypeCode.POSTPONE
        | EditIntentTypeCode.REMOVE_VIA_CORRECTION
        | null;
    invoiceCurrencyTypeCode: string | null;
    amountInInvoiceCurrency: number | null;
    currencyRate: number | null;
    expense: ExpenseDto;
    isMirrored: boolean;
    isHidden: boolean;
    isRemoved: boolean;
    calculationLog?: InvoiceExpenseCalculationLog;
}

interface InvoiceExpenseCalculationLogBase {
    sign: number;
    isNonVat: boolean;
    vatRate: number;
    vat: number;
    initialAmount: number;
    groupTotalAmount: number;
    amountAfterExpenseAdjustments: number;
    expenseMatterAdjustmentProportion: number;
    matterAdjustmentAmount: number;
    expenseMatterAdjustmentAmount: number;
    expenseAmountBeforeMatterDiscount: number;
    matterDiscountRate: number;
    expenseMatterDiscountAdjustmentAmount: number;
    finalAmount: number;
}

interface InvoiceExpenseCalculationLog extends InvoiceExpenseCalculationLogBase {
    paymentCurrency?: InvoiceExpenseCalculationLogBase;
}

export interface InvoiceExpenseUpdateDto {
    id: number;
    employee: NamedEntityMinDto | null;
    expenseDate: string | null;
    matter: NamedEntityMinDto | null;
    description: string | null;
    isNonBillable: boolean;
    expenseTypeCode: string | null;
    expenseAmount: number | null;
    editIntentTypeCode?:
        | EditIntentTypeCode.MOVE_TO_ANOTHER_CASE
        | EditIntentTypeCode.MARK_AS_NONBILLABLE
        | EditIntentTypeCode.POSTPONE
        | EditIntentTypeCode.REMOVE_VIA_CORRECTION
        | null;
    discountAmount: number | null;
    discountTypeCode: string | null;
    invoiceId: number;
    isMirrored: boolean;
    isHidden: boolean;
}

export interface InvoiceExpenseForm {
    id: number;
    employee: NamedEntityMinDto | null;
    expenseDate: string | null;
    description: string;
    isNonBillable: boolean;
    expenseType: { label: string; value: string } | null;
    expenseAmount: number | null;
    currencyType: { label: string; value: string } | null;
    discountAmount: number | null;
    discountTypeCode: string | null;
    matter: NamedEntityMinDto | null;
    newMatter: NamedEntityMinDto | null;
    editIntentTypeCode:
        | EditIntentTypeCode.MOVE_TO_ANOTHER_CASE
        | EditIntentTypeCode.MARK_AS_NONBILLABLE
        | EditIntentTypeCode.POSTPONE
        | EditIntentTypeCode.REMOVE_VIA_CORRECTION
        | null;
    expense: ExpenseDto | null;
    selected: boolean;
    isMirrored: boolean;
    isHidden: boolean;
}

export enum InvoiceTimecardIssueType {
    MISSING_JOB_ROLE = 'MISSING_JOB_ROLE',
    MISSING_HOURLY_RATE = 'MISSING_HOURLY_RATE',
}

export interface InvoiceTimecard {
    id: number;
    employee: EmployeeListItemMinDto;
    timecardDate: string;
    timecardActivityTypeCode?: string;
    remunerationCalcTypeCode: RemunerationCalcType;
    description?: string;
    hourlyPrice?: number;
    hourlyPriceInPaymentCurrency?: number;
    discountAmount?: number;
    totalAmount?: number;
    discountTypeCode?: string;
    languageTypeCode?: string;
    isNonBillable: boolean;
    preserveNonBillableHours: boolean;
    totalTimeInSeconds?: number;
    totalTimeInHours?: string;
    totalBeforeAdjustment?: number;
    totalBeforeAdjustmentInPaymentCurrency?: number;
    billedTimeInSeconds: number;
    billingUpliftValueFactors: string[] | null;
    valueFactorsNotes: string | null;
    notes: string | null;
    adjustmentAmount: number;
    multiplierTypeCode: string;
    multiplierRate: string;
    isMirrored: boolean;
    isHidden: boolean;
    isRemoved: boolean;
    discountRate: number;
    manualAdjustmentAmount?: number;
    manualAdjustmentAmountInPaymentCurrency?: number;
    paymentCurrencyTypeCode?: string;
    matter: MatterListItemMin;
    newMatter?: MatterListItemMin;
    matterAssignment?: NamedEntityMinDto;
    newMatterAssignment?: NamedEntityMinDto;
    editIntentTypeCode?:
        | EditIntentTypeCode.MOVE_TO_ANOTHER_CASE
        | EditIntentTypeCode.MARK_AS_NONBILLABLE
        | EditIntentTypeCode.REMOVE_VIA_CORRECTION
        | EditIntentTypeCode.POSTPONE;
    timecard: TimecardBasicDto;
    positionText?: string;
    amountAfterTimecardAdjustments?: number;
    calculationLog?: InvoiceTimecardCalculationLog;
    remunerationTimeInSeconds?: number;
    timecardForLabel?: string;
    issues?: InvoiceTimecardIssueType[];
}

interface InvoiceTimecardCalculationLogBase {
    vatRate: number;
    vat: number;
    usedTimeInHours: number;
    billedTimeInHours: number;
    hourlyRate: number;
    multiplierRate: number;
    initialAmount: number;
    groupTotalAmount: number;
    timecardMatterAdjustmentProportion: number;
    applicableMatterTotalAdjustment: number;
    timecardDiscountRate: number;
    timecardDiscountAdjustmentAmount: number;
    timecardCashAdjustmentAmount: number;
    amountAfterTimecardAdjustments: number;
    timecardMatterCashAdjustmentAmount: number;
    timecardAmountBeforeMatterDiscount: number;
    matterDiscountAdjustmentRate: number;
    matterDiscountAdjustmentAmount: number;
    finalAmount: number;
}

interface InvoiceTimecardCalculationLog extends InvoiceTimecardCalculationLogBase {
    paymentCurrency?: InvoiceTimecardCalculationLogBase;
}

export interface InvoiceTimecardForm {
    id: number;
    employee: NamedEntityMinDto | null;
    timecardDate: string | null;
    timecardActivityType: { label: string; value: string } | null;
    remunerationCalcType: RemunerationCalcType | null;
    description: string | null;
    totalTimeInSeconds: number | null;
    adjustmentAmount: number;
    totalTimeInHours: string | null;
    billedTimeInSeconds: number | null;
    billedTimeInHours: string | null;
    hourlyPrice: number | null;
    isHourlyPriceManuallyAdjusted: boolean | null;
    discountAmount: number | null;
    totalAmount: number | null;
    discountTypeCode: string | null;
    multiplierType: { label: string; value: string } | null;
    multiplierRate: string;
    isMirrored: boolean;
    isHidden: boolean;
    discountRate: number | null;
    manualAdjustmentAmount: number | null;
    matter: MatterListItemMin | null;
    newMatter: NamedEntityMinDto | null;
    matterAssignment: NamedEntityMinDto | null;
    newMatterAssignment: NamedEntityMinDto | null;
    editIntentTypeCode:
        | EditIntentTypeCode.MOVE_TO_ANOTHER_CASE
        | EditIntentTypeCode.MARK_AS_NONBILLABLE
        | EditIntentTypeCode.POSTPONE
        | EditIntentTypeCode.REMOVE_VIA_CORRECTION
        | null;
    isNonBillable: boolean;
    preserveNonBillableHours: boolean;
    timecard: TimecardBasicDto | null;
    selected: boolean;
    totalBeforeAdjustment: number | string;
    jobPositionName: string | null;
    isPositionTextInputEnabled: boolean;
    positionText: string | null;
    remunerationTimeInSeconds: number | null;
    remunerationTimeInHours: string | null;
    remEqualsUsedTime: boolean;
    isTimecardForInputEnabled: boolean;
    timecardForLabel?: string | null;
    billingUpliftValueFactors: { label: string; value: string }[];
    valueFactorsNotes: string | null;
    notes: string | null;
}

export interface InvoiceTimecardUpdateDto {
    id: number;
    employee: { id: number } | null;
    timecardDate: string | null;
    timecardActivityTypeCode: string | null;
    description: string | null;
    billedTimeInSeconds: number | null;
    multiplierTypeCode: string | null;
    multiplierRate: string;
    isHidden: boolean | null;
    isMirrored: boolean | null;
    manualAdjustmentAmount: number | null;
    discountRate: number | null;
    matter: { id: number } | null;
    matterAssignment: { id: number } | null;
    editIntentTypeCode:
        | EditIntentTypeCode.MOVE_TO_ANOTHER_CASE
        | EditIntentTypeCode.MARK_AS_NONBILLABLE
        | EditIntentTypeCode.POSTPONE
        | EditIntentTypeCode.REMOVE_VIA_CORRECTION
        | null;
    isNonBillable: boolean;
    preserveNonBillableHours: boolean;
    invoiceId: number;
    positionText: string | null;
    remunerationCalcTypeCode: RemunerationCalcType | null;
    remunerationTimeInSeconds: number | null;
    hourlyPrice: number | null;
    isHourlyPriceManuallyAdjusted: boolean | null;
    timecardForLabel?: string | null;
    billingUpliftValueFactors: string[];
    valueFactorsNotes: string | null;
    notes: string | null;
}

interface InvoiceRow {
    id: number;
    description: string | null;
    totalAmount: number | string | null;
    invoiceRowTypeCode: string | null;
    currencyTypeCode: string | null;
    createdAt: Date;
    isRemoved: boolean;
}

export interface InvoiceMatterForm {
    matterId: number;
    discountRate: number | null;
    discountAmount: string | null;
    adjustmentAmount: number | null;
    invoiceExpenses: InvoiceExpenseForm[];
    invoiceTimecards: InvoiceTimecardForm[];
    invoiceAssignments: InvoiceTimecardAssignment[];
    invoiceMatterName: string | null;
    invoiceMatterNameLocalLanguage: string | null;
    specificationInvoiceMatterName: string | null;
    specificationInvoiceMatterNameLocalLanguage: string | null;
    adjustmentAmountForFees: number | null;
    adjustmentAmountForExpensesWithoutVat: number | null;
    adjustmentAmountForExpensesWithVat: number | null;
    capRemainingBeforeInvoice: number | null;
    areCostsCapped: boolean | null;
    areCostsDiscounted: boolean | null;
}

export interface InvoiceMatterUpdateDto {
    invoiceId: number;
    matterId: number;
    invoiceMatterName: string | null;
    invoiceMatterNameLocalLanguage: string | null;
    specificationInvoiceMatterName: string | null;
    specificationInvoiceMatterNameLocalLanguage: string | null;
}

export interface InvoiceMatterStatusUpdateDto {
    invoiceId: number;
    matterId: number;
    invoiceStatusTypeCode: InvoiceStatusType;
    transitionActionTypeCode: InvoiceTransactionActionType;
    transitionComment?: string;
}

export interface InvoiceMatterAdjustmentDto {
    invoiceId: number;
    matterId: number;
    discountRate: number | null;
    adjustmentAmount: number | null;
    assignments?: {
        assignmentId?: number;
        adjustmentAmount: number | null;
    }[];
    adjustmentAmountForFees: number | null;
    adjustmentAmountForExpensesWithoutVat: number | null;
    adjustmentAmountForExpensesWithVat: number | null;
    capRemainingBeforeInvoice: number | null;
    areCostsCapped: boolean | null;
    areCostsDiscounted: boolean | null;
}

export interface InvoiceMatterBulkDiscountDto {
    invoiceId: number;
    discountRate: number | null;
    areCostsDiscounted: boolean;
}

export interface InvoiceTimecardBulkChangeForm {
    amountBefore: number | string;
    amountAfter: number | string;
    billedTimeInHours: string;
    adjustPct: number | string;
}

export interface InvoiceTimecardBulkUpdateDto {
    id: number;
    billedTimeInSeconds: number | null;
    hourlyPrice: number | null;
    isNonBillable: boolean | null;
    preserveNonBillableHours: boolean | null;
    remunerationCalcTypeCode?: string | null;
    newMatterId: number | null;
    manualAdjustmentAmount: number | null;
    newMatterAssignmentId: number | null;
    editIntentTypeCode:
        | EditIntentTypeCode.MOVE_TO_ANOTHER_CASE
        | EditIntentTypeCode.MARK_AS_NONBILLABLE
        | EditIntentTypeCode.POSTPONE
        | EditIntentTypeCode.REMOVE_VIA_CORRECTION
        | null;
    timecardForLabel?: string | null;
}

export interface InvoiceExpenseBulkUpdateDto {
    id: number;
    isNonBillable: boolean | null;
    newMatterId: number | null;
    editIntentTypeCode:
        | EditIntentTypeCode.MOVE_TO_ANOTHER_CASE
        | EditIntentTypeCode.MARK_AS_NONBILLABLE
        | EditIntentTypeCode.POSTPONE
        | EditIntentTypeCode.REMOVE_VIA_CORRECTION
        | null;
}

export interface InvoiceMatterNameDetailsForm {
    matterId: number;
    invoiceMatterName: string | null;
    invoiceMatterNameLocalLanguage: string | null;
    specificationInvoiceMatterName: string | null;
    specificationInvoiceMatterNameLocalLanguage: string | null;
}
